import { emptyProgress, Progress, sanitizeProgress } from './progress';

export interface ProgressStorage {
  load(): Progress;
  save(p: Progress): void;
}

const copy = (p: Progress) => sanitizeProgress(JSON.parse(JSON.stringify(p)));

export class MemoryProgressStorage implements ProgressStorage {
  private value = emptyProgress();
  load() { return copy(this.value); }
  save(p: Progress) { this.value = copy(p); }
}

type KeyValue = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): KeyValue | null {
  try {
    return globalThis.localStorage ?? null; // the getter itself throws when site data is blocked
  } catch {
    return null;
  }
}

/**
 * Progress in localStorage. Every access is guarded: private windows, blocked storage and quota errors fall back
 * to an in-memory copy, so the game keeps working for the session (`persistent` turns false).
 */
export class LocalProgressStorage implements ProgressStorage {
  private readonly memory = new MemoryProgressStorage();
  private readonly store: KeyValue | null;
  persistent: boolean;

  constructor(readonly key = 'megamx.progress.v1', store?: KeyValue | null) {
    this.store = store === undefined ? defaultStorage() : store;
    this.persistent = this.store !== null;
    this.load();
  }

  load(): Progress {
    if (this.store && this.persistent) {
      try {
        const raw = this.store.getItem(this.key);
        if (raw !== null) this.memory.save(sanitizeProgress(JSON.parse(raw)));
      } catch (e) {
        if (!(e instanceof SyntaxError)) this.persistent = false; // corrupt JSON: keep the store, start fresh
      }
    }
    return this.memory.load();
  }

  save(p: Progress): void {
    this.memory.save(p);
    if (!this.store || !this.persistent) return;
    try {
      this.store.setItem(this.key, JSON.stringify(p));
    } catch {
      this.persistent = false;
    }
  }
}
